import { isObject } from '@/helpers/utils';

/**
 * @typedef {object} DownloadAndSaveFileResponse
 * @property {boolean} success - Статус выполнения запроса
 * @property {string} [fileName] - Название файла (опционально). Если не указано придёт в ответе, но без гарантий, что как в ссылке
 * @property {'invalid_file_name'} [error] - Не удалось извлечь имя файла из ссылки
 */

/**
 * @typedef {'invalid_url_format' | 'network_timeout' | 'network_error_or_server_issue' | 'unexpected_error' | 'download_not_found'} ProgressError
 */

/**
 * @typedef {'downloading' | 'saving' | 'completed' | 'failed'} CachingStatus
 */

/**
 * @typedef {object} GetDownloadProgressResponse
 * @property {number} totalSize
 * @property {number} downloadSize
 * @property {CachingStatus} status
 * @property {ProgressError} [error]
 * @property {string} [errorMessage]
 */

/**
 * @typedef {object} CachedFileInfo
 * @property {string} fileName
 * @property {number} totalSize
 * @property {CachingStatus} status
 * @property {string | undefined} errorMessage
 * @property {string} fileUrl
 */

const LOCALSTORAGE_STATE_KEY = 'CacheService';

const CACHEABLE_MEDIA_TYPES = Object.freeze({
  jpeg: true,
  png: true,
  mp4: true,
  mpeg: true,
});

const MAX_TRIES = 3;

let instance = undefined;

function sleep(timer) {
  return new Promise(resolve => {
    window.setTimeout(() => {
      resolve()
    }, timer)
  })
}

export default class CacheService {
  constructor() {
    // Немного странных костылей, что бы не ломать поддержку старых версий браузеров
    // Идея в том, что бы использовать CacheService как singleton
    if (instance) {
      throw new Error('CacheService already exists');
    }

    this.caching = false;
    this.cachingProgress = { totalSize: 0, downloadSize: 0 };

    /** @type {Record<string, CachedFileInfo>} */
    this.cachedFiles = {};
    this.lsCachedFiles = this._readLocalStorage();

    this.cache = this._cache.bind(this);
    this.cleanUpCache = this._cleanUpCache.bind(this);

    instance = this;

    this.deleteAllFiles = this._deleteAllFiles.bind(this);
  }

  static get getInstance() {
    return instance;
  }

  get canUseCache() {
    return 'FileAPI' in window;
  }

  get cachedFilesNames() {
    const result = [];

    for (const fileUrl in this.cachedFiles) {
      result.push(this.cachedFiles[fileUrl].fileName);
    }

    return result;
  }

  get failedCachedFiles() {
    const result = [];

    for (const fileUrl in this.cachedFiles) {
      if (this.cachedFiles[fileUrl].status === 'failed') {
        result.push(this.cachedFiles[fileUrl]);
      }
    }

    return result;
  }

  _readLocalStorage() {
    const result = {};
    let savedData;

    try {
      const lsData = localStorage.getItem(LOCALSTORAGE_STATE_KEY);
      savedData = JSON.parse(lsData);
    } catch (e) {
      // eat it
    }

    // Нет данных или не распарсилось или не объект
    if (!savedData || !isObject(savedData)) {
      return result;
    }

    // Проверка включений на соответствие ожидаениям. Любые поломанные записи игнорирую
    for (const fileUrl in savedData) {
      if (
        !savedData[fileUrl].fileName ||
        typeof savedData[fileUrl].fileName !== 'string'
      ) {
        continue;
      }
      if (typeof savedData[fileUrl].totalSize !== 'number') {
        continue;
      }
      if (
        savedData[fileUrl].status !== 'downloading' &&
        savedData[fileUrl].status !== 'saving' &&
        savedData[fileUrl].status !== 'completed' &&
        savedData[fileUrl].status !== 'failed'
      ) {
        continue;
      }
      if (
        !savedData[fileUrl].fileUrl ||
        typeof savedData[fileUrl].fileUrl !== 'string'
      ) {
        continue;
      }

      result[fileUrl] = {
        fileName: savedData[fileUrl].fileName,
        totalSize: savedData[fileUrl].totalSize,
        status: savedData[fileUrl].status,
        fileUrl: savedData[fileUrl].fileUrl,
      };
    }

    return result;
  }

  _updateLocalStorage() {
    localStorage.setItem(
      LOCALSTORAGE_STATE_KEY,
      JSON.stringify(this.cachedFiles),
    );
  }

  _clearLocalStorage() {
    localStorage.removeItem(LOCALSTORAGE_STATE_KEY);
  }

  _testCanUseCache(methodUsed) {
    if (!this.canUseCache) {
      throw new Error(
        `CacheService::${methodUsed}: Caching functions does no exist`,
      );
    }
  }

  _fileExists(fileName) {
    this._testCanUseCache('fileExists');
    return window.FileAPI.fileExists(fileName);
  }

  _deleteFile(fileName) {
    this._testCanUseCache('deleteFile');
    return window.FileAPI.deleteFile(fileName);
  }

  _listFiles() {
    this._testCanUseCache('listFiles');
    return JSON.parse(window.window.FileAPI.listFiles());
  }

  _deleteAllFiles() {
    this._testCanUseCache('deleteAllFiles');
    this._clearLocalStorage();
    return window.FileAPI.deleteAllFiles();
  }

  /**
   * @param {string} fileUrl
   * @param {string} [fileName = undefined]
   * @returns {DownloadAndSaveFileResponse}
   * @private
   */
  _downloadAndSaveFile(fileUrl, fileName = undefined) {
    this._testCanUseCache('downloadAndSaveFile');
    const response = window.FileAPI.downloadAndSaveFile(
      fileUrl,
      fileName || this._getFileName(fileUrl),
    );
    return JSON.parse(response);
  }

  /**
   * @param fileUrl
   * @returns {GetDownloadProgressResponse}
   * @private
   */
  _getDownloadProgress(fileUrl) {
    this._testCanUseCache('getDownloadProgress');
    return JSON.parse(window.FileAPI.getDownloadProgress(fileUrl));
  }

  /**
   * @param {string} fileName
   * @returns {string}
   * @private
   */
  _getFileUrl(fileName) {
    this._testCanUseCache('getFileUrl');
    return window.FileAPI.getFileUrl(fileName);
  }

  _listFileNames() {
    this._testCanUseCache('listFiles');
    const fileList = this._listFiles();
    return fileList.reduce((acc, { name }) => {
      acc.push(name);
      return acc;
    }, []);
  }

  _isCacheablePlaylistItem(item) {
    return (
      isObject(item) &&
      item.item_type === 'media' &&
      isObject(item.media) &&
      CACHEABLE_MEDIA_TYPES[item.media.type] &&
      isObject(item.media.file) &&
      typeof item.media.file.file_url === 'string'
    );
  }

  _getFileName(url) {
    const urlObj = new URL(url);
    const fileName = urlObj.pathname.split('/').pop();
    return decodeURIComponent(fileName);
  }

  _checkLsFile(fileUrl) {
    return (
      fileUrl in this.lsCachedFiles &&
      this.lsCachedFiles[fileUrl].status === 'completed'
    );
  }

  _updateCachingProgress() {
    let totalSize = 0;
    let downloadSize = 0;

    for (const fileUrl in this.cachedFiles) {
      // Для этих статусов считаю, что файл загружен полностью, что бы корректно показать прогресс
      if (
        this.cachedFiles[fileUrl].status === 'completed' ||
        this.cachedFiles[fileUrl].status === 'failed'
      ) {
        totalSize += this.cachedFiles[fileUrl].totalSize;
        downloadSize += this.cachedFiles[fileUrl].totalSize;
        continue;
      }

      const response = this._getDownloadProgress(fileUrl);
      this.cachedFiles[fileUrl].status = response.status;
      this.cachedFiles[fileUrl].totalSize = response.totalSize;

      if (this.cachedFiles[fileUrl].status === 'completed') {
        this.cachedFiles[fileUrl].fileUrl = this._getFileUrl(
          this.cachedFiles[fileUrl].fileName,
        );
      }

      totalSize += response.totalSize;
      downloadSize += response.downloadSize;
    }

    this.cachingProgress.totalSize = totalSize;
    this.cachingProgress.downloadSize = downloadSize;

    this._updateLocalStorage();
  }

  _waitDownloadAndSaveFiles() {
    return new Promise((resolve) => {
      // Выставит реальные данные о прогрессе кеширования, что бы показатели totalSize: 0 и downloadSize: 0 стали реальными
      this._updateCachingProgress();

      const interval = window.setInterval(() => {
        this._updateCachingProgress();

        let filesDownloaded =
          this.cachingProgress.totalSize === this.cachingProgress.downloadSize;
        // Пока загрузка не завершена нет смысла проверять статусы
        if (!filesDownloaded) {
          return;
        }

        const cachingDone = Object.values(this.cachedFiles).every(
          ({ status }) => status === 'completed' || status === 'failed',
        );

        if (cachingDone) {
          resolve();
          window.clearInterval(interval);
        }
      }, 100);
    });
  }

  async _testCacheFailure() {
    let i = 0;
    while (this.failedCachedFiles.length && i < MAX_TRIES) {
      for (const fileInfo of this.failedCachedFiles) {
        // Будет работать с использованием fileUrl из CachedFileInfo, т.к. ссылка подменяется только при получении
        // статуса completed, а кеширование свалилось в failed, что значит статус completed никогда и не был назначен
        this._downloadAndSaveFile(fileInfo.fileUrl, fileInfo.fileName);
        fileInfo.status = 'downloading'
      }

      await this._waitDownloadAndSaveFiles();

      i += 1;
    }
  }

  async _cache(data) {
    if (
      !data ||
      !isObject(data) ||
      !('items' in data) ||
      !Array.isArray(data.items) ||
      !('playlistParam' in data)
    ) {
      return data;
    }

    const viableItems = data.items.filter(this._isCacheablePlaylistItem);
    if (!viableItems.length) {
      return data;
    }

    this.caching = true;
    this.cachingProgress.totalSize = viableItems.length;
    this.cachingProgress.downloadSize = 0;

    for (const item of viableItems) {
      const isItemCacheable = this._isCacheablePlaylistItem(item);
      if (!isItemCacheable) {
        continue;
      }

      const fileName = this._getFileName(item.media.file.file_url);
      const isFileExist = this._fileExists(fileName);
      const lsFileValid = this._checkLsFile(item.media.file.file_url);

      // По скольку нет возможности уточнить полностью ли был скачан файл, важно убедиться, что в LS хранится инфо по файлу
      if (!isFileExist || !lsFileValid) {
        // Не проверяю на потенциально возможную ошибку, т.к. ошибка может упатсь только если не удалось получить
        // название файла из ссылки, а я передаю его экплицитно
        this._downloadAndSaveFile(item.media.file.file_url, fileName);
      }

      this.cachedFiles[item.media.file.file_url] = {
        fileName,
        totalSize: lsFileValid ? this.lsCachedFiles[item.media.file.file_url].totalSize : 0,
        fileUrl: lsFileValid ? this.lsCachedFiles[item.media.file.file_url].fileUrl : '',
        errorMessage: undefined,
        status: lsFileValid
          ? this.lsCachedFiles[item.media.file.file_url].status
          : 'downloading',
      };
    }

    await this._waitDownloadAndSaveFiles();

    await this._testCacheFailure();

    for (const item of viableItems) {
      // Подменяю ссылки только тех элементов которые успешно прошли синхронизацию. Если произошла ошибка, ссылка
      // остаётся оригинальной, что бы воспроизведение медиа не поломалось
      if (this.cachedFiles[item.media.file.file_url].status === 'completed') {
        item.media.file.file_url = this.cachedFiles[
          item.media.file.file_url
        ].fileUrl;
      }
    }

    this.caching = false;

    return data;
  }

  _cleanUpCache() {
    const cachedFilesNames = this.cachedFilesNames;
    const savedFilesNames = this._listFileNames();
    for (const fileName of savedFilesNames) {
      const isUnknownFile = cachedFilesNames.indexOf(fileName) === -1;
      if (isUnknownFile) {
        this._deleteFile(fileName);
      }
    }
  }
}
